import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import BottomSheetContext, {
  OpenChangeReason,
  useOptionalBottomSheetContext,
} from "./bottomSheetContext";
import {
  applyNestedClose,
  applyNestedDrag,
  applyNestedOpen,
  clearNestedTransform,
} from "./nestedSheet";
import { SnapPoint, resolveSnapPoints, ResolvedSnapPoints } from "./snapPoints";
import { useControlledState } from "./useControlledState";
import { useScrollLock } from "./useScrollLock";
import { useVirtualKeyboard } from "./useVirtualKeyboard";

export type RootProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean, reason: OpenChangeReason) => void;
  dismissible?: boolean;
  modal?: boolean;
  /** Default true. Lift sheet above software keyboard while a field is focused. */
  repositionInputs?: boolean;
  snapPoints?: SnapPoint[];
  snapPoint?: SnapPoint;
  defaultSnapPoint?: SnapPoint;
  onSnapPointChange?: (snapPoint: SnapPoint) => void;
  children?: React.ReactNode;
};

type ViewportMetrics = {
  viewportHeight: number;
  rootFontSize: number;
};

const NEST_CLEAR_MS = 500;

function getViewportMetrics(): ViewportMetrics {
  if (typeof window === "undefined") {
    return { viewportHeight: 0, rootFontSize: 16 };
  }
  const rootFontSize =
    typeof document !== "undefined"
      ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      : 16;
  return { viewportHeight: window.innerHeight, rootFontSize };
}

export function Root({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  dismissible = true,
  modal = true,
  repositionInputs = true,
  snapPoints,
  snapPoint: snapPointProp,
  defaultSnapPoint,
  onSnapPointChange,
  children,
}: RootProps) {
  const parent = useOptionalBottomSheetContext();

  const [open, setOpenValue] = useControlledState({
    value: openProp,
    defaultValue: defaultOpen,
  });

  const [mounted, setMounted] = useState(open);
  const [dragging, setDragging] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [nestedOpenCount, setNestedOpenCount] = useState(0);
  const [titleId, setTitleId] = useState<string | undefined>(undefined);
  const [descriptionId, setDescriptionId] = useState<string | undefined>(undefined);
  const [viewportMetrics, setViewportMetrics] = useState(getViewportMetrics);

  const popupRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(open);
  const hadNestedRef = useRef(false);
  const nestedClearTimerRef = useRef<number | null>(null);
  const activeSnapOffsetRef = useRef(0);

  const hasSnapPoints = Boolean(snapPoints && snapPoints.length > 0);

  const resolvedSnapPoints: ResolvedSnapPoints | null = useMemo(() => {
    if (!snapPoints || snapPoints.length === 0) {
      return null;
    }
    return resolveSnapPoints(
      snapPoints,
      viewportMetrics.viewportHeight,
      viewportMetrics.rootFontSize,
    );
  }, [snapPoints, viewportMetrics]);

  const topSnapPoint = resolvedSnapPoints?.points[resolvedSnapPoints.points.length - 1];
  const snapControlled = snapPointProp !== undefined;

  const [snapPoint, setSnapPointValue] = useControlledState<SnapPoint>({
    value: snapPointProp,
    defaultValue: defaultSnapPoint ?? topSnapPoint ?? 1,
  });

  const setOpen = useCallback(
    (next: boolean, reason: OpenChangeReason) => {
      if (
        !next &&
        !dismissible &&
        (reason === "backdrop-press" || reason === "escape-key" || reason === "swipe-down")
      ) {
        return;
      }
      setOpenValue(next);
      onOpenChange?.(next, reason);
    },
    [dismissible, onOpenChange, setOpenValue],
  );

  const setSnapPoint = useCallback(
    (next: SnapPoint) => {
      setSnapPointValue(next);
      onSnapPointChange?.(next);
    },
    [onSnapPointChange, setSnapPointValue],
  );

  useEffect(() => {
    if (open) {
      setMounted(true);
    }
  }, [open]);

  useEffect(() => {
    if (!hasSnapPoints) {
      return;
    }

    function onViewportChange() {
      setViewportMetrics(getViewportMetrics());
    }

    window.addEventListener("resize", onViewportChange);
    window.addEventListener("orientationchange", onViewportChange);
    return () => {
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("orientationchange", onViewportChange);
    };
  }, [hasSnapPoints]);

  useLayoutEffect(() => {
    const opened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!opened || snapControlled || !resolvedSnapPoints) {
      return;
    }

    const initial = defaultSnapPoint ?? resolvedSnapPoints.points[resolvedSnapPoints.points.length - 1];
    setSnapPointValue(initial);
  }, [open, snapControlled, resolvedSnapPoints, defaultSnapPoint, setSnapPointValue]);

  useScrollLock(mounted && modal);

  useVirtualKeyboard({
    enabled: repositionInputs,
    open,
    mounted,
    popupRef,
    setKeyboardOpen,
  });

  const activeSnapIndex = useMemo(() => {
    if (!resolvedSnapPoints) {
      return -1;
    }
    const index = resolvedSnapPoints.points.findIndex(point => point === snapPoint);
    return index >= 0 ? index : resolvedSnapPoints.points.length - 1;
  }, [resolvedSnapPoints, snapPoint]);

  const activeSnapOffset =
    resolvedSnapPoints && activeSnapIndex >= 0 ? resolvedSnapPoints.offsets[activeSnapIndex] : 0;

  activeSnapOffsetRef.current = activeSnapOffset;

  const onNestedOpenChange = useCallback((nextOpen: boolean) => {
    setNestedOpenCount(prev => (nextOpen ? prev + 1 : Math.max(0, prev - 1)));
  }, []);

  const onNestedDrag = useCallback((percentageDragged: number) => {
    applyNestedDrag(popupRef.current, activeSnapOffsetRef.current, percentageDragged);
  }, []);

  const onNestedRelease = useCallback((stillOpen: boolean) => {
    if (stillOpen) {
      applyNestedOpen(popupRef.current, activeSnapOffsetRef.current);
    }
  }, []);

  useEffect(() => {
    const hasNested = nestedOpenCount > 0;
    const el = popupRef.current;

    if (hasNested && !hadNestedRef.current) {
      if (nestedClearTimerRef.current !== null) {
        window.clearTimeout(nestedClearTimerRef.current);
        nestedClearTimerRef.current = null;
      }
      applyNestedOpen(el, activeSnapOffsetRef.current);
    } else if (!hasNested && hadNestedRef.current) {
      applyNestedClose(el, activeSnapOffsetRef.current);
      nestedClearTimerRef.current = window.setTimeout(() => {
        nestedClearTimerRef.current = null;
        clearNestedTransform(popupRef.current);
      }, NEST_CLEAR_MS);
    }

    hadNestedRef.current = hasNested;
  }, [nestedOpenCount]);

  useEffect(() => {
    return () => {
      if (nestedClearTimerRef.current !== null) {
        window.clearTimeout(nestedClearTimerRef.current);
        nestedClearTimerRef.current = null;
      }
    };
  }, []);

  const parentOnNestedOpenChange = parent?.onNestedOpenChange;
  const nestDepth = parent ? parent.nestDepth + 1 : 0;
  const isNested = parent != null;

  useEffect(() => {
    if (!parentOnNestedOpenChange || !open) {
      return;
    }
    parentOnNestedOpenChange(true);
    return () => {
      parentOnNestedOpenChange(false);
    };
  }, [open, parentOnNestedOpenChange]);

  const contextValue = useMemo(
    () => ({
      open,
      setOpen,
      mounted,
      setMounted,
      dragging,
      setDragging,
      titleId,
      setTitleId,
      descriptionId,
      setDescriptionId,
      popupRef,
      backdropRef,
      dismissible,
      modal,
      previousFocusRef,
      snapPoints: resolvedSnapPoints,
      snapPoint: hasSnapPoints ? snapPoint : undefined,
      setSnapPoint,
      activeSnapIndex,
      activeSnapOffset,
      keyboardOpen,
      isNested,
      nestDepth,
      nestedOpenCount,
      onNestedOpenChange,
      onNestedDrag,
      onNestedRelease,
      notifyParentNestedDrag: parent?.onNestedDrag,
      notifyParentNestedRelease: parent?.onNestedRelease,
    }),
    [
      open,
      setOpen,
      mounted,
      dragging,
      titleId,
      descriptionId,
      dismissible,
      modal,
      resolvedSnapPoints,
      hasSnapPoints,
      snapPoint,
      setSnapPoint,
      activeSnapIndex,
      activeSnapOffset,
      keyboardOpen,
      isNested,
      nestDepth,
      nestedOpenCount,
      onNestedOpenChange,
      onNestedDrag,
      onNestedRelease,
      parent?.onNestedDrag,
      parent?.onNestedRelease,
    ],
  );

  return (
    <BottomSheetContext.Provider value={contextValue}>{children}</BottomSheetContext.Provider>
  );
}
